import { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
  Select,
  Checkbox,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchFileCategoryById,
  createFileCategory,
  updateFileCategory,
} from "services/files-category.service";
import { fetchCategoryFilesDetails } from "containers/document/category-files/catFiles.Slice";
import { fetchCategoryDetails } from "containers/document/category-tree/category-tree.Slice";
import { fetchFilesCategories } from "containers/files-categories/files-categories-container.slice";
import { AppDispatch } from "configureStore";
import FileCategorySingleSelect from "components/file-category-single-select/file-category-single-select";
import MultipleUserSelect from "features/multi-user-select/multi-user-select";
import MultipleSignatureSelect from "features/multi-signature-type-select/multi-signature-type-select";

interface AddFilesCategoryFormProps {
  onSubmit?: (result: any) => void;
  id?: string;
  parentId?: string | null;
}

const AddFilesCategoryForm: React.FC<AddFilesCategoryFormProps> = ({
  onSubmit,
  id,
  parentId,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();
  const [subject, setSubject] = useState<string>("customer");
  const [title, setTitle] = useState<string>("");
  const [type, setType] = useState<string>("external");
  const [isShared, setIsShared] = useState<boolean>(false);
  const [isVisibleInProfile, setIsVisibleInProfile] = useState<boolean>(false);
  const [parentCategory, setParentCategory] = useState<string[]>([]);
  const [order, setOrder] = useState<string>("");
  const [requiredWithinDays, setRequiredWithDays] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [errMsg, setErrMsg] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [parentCategoryError, setParentCategoryError] = useState<string | null>(
    null
  );
  const [reviewRequiredUser, setReviewRequiredUser] = useState<any>([]);
  const [responsibleUser, setResponsibleUser] = useState<any>([]);
  const [signatureTypes, setSignatureTypes] = useState<any>([]);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!title) newErrors.title = t("title_required");
    if (!subject) newErrors.subject = t("subject_required");
    if (!type) newErrors.type = t("type_required");
    if (order === undefined || order === null || order === "")
      newErrors.order = t("order_required");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (parentId) {
      setParentCategory([parentId]);
    }
  }, [parentId]);

  useEffect(() => {
    if (id && parentCategory[0] === id) {
      setParentCategoryError(t("cannot_select_same_category"));
    } else {
      setParentCategoryError(null);
    }
  }, [parentCategory, id, t]);

  const fetchData = useCallback(
    async (id: string) => {
      const data = await fetchFileCategoryById(companyId, id);
      setSubject(data.subject);
      setTitle(data.title);
      setParentCategory(data.parentCategory ? [data.parentCategory] : []);
      setType(data.type);
      setOrder(data.order);
      setRequiredWithDays(data.requiredWithinDays);
      setIsShared(data.isShared);
      setIsVisibleInProfile(data.isVisibleInProfile);
      setReviewRequiredUser(data.reviewRequiredUser || []);
      setResponsibleUser(data.responsibleUser || []);
      setSignatureTypes(data.signatureTypes || []);
    },
    [companyId]
  );

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  const handleFormSubmit = async () => {
    if (!validateForm() || parentCategoryError) return;
    const parentcategoryId =
      parentCategory && parentCategory?.length ? parentCategory[0] : null;
    const formData = {
      id,
      subject,
      title,
      parentCategory: parentcategoryId,
      type,
      order,
      isShared,
      isVisibleInProfile,
      reviewRequiredUser,
      responsibleUser,
      requiredWithinDays,
      signatureTypes,
    };
    const action = id ? updateFileCategory : createFileCategory;
    try {
      setIsLoading(true);
      const result = await action(formData, companyId);
      if (companyId) {
        dispatch(fetchCategoryDetails({ companyId, source: "document" }));
        dispatch(fetchFilesCategories(companyId));
        if (id && companyId)
          dispatch(
            fetchCategoryFilesDetails({
              companyId,
              catId: id,
              source: "document",
            })
          );
      }
      setSuccessMessage("ok");
      if (onSubmit) {
        onSubmit(result);
      }
    } catch (e) {
      console.error(e);
      setErrMsg("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = useCallback(
    (setter: React.Dispatch<React.SetStateAction<boolean>>) =>
      (newChecked: boolean) => {
        setter(newChecked);
      },
    []
  );

  const typeOptions = [
    { label: t("internal"), value: "internal" },
    { label: t("external"), value: "external" },
  ];

  return (
    <Form onSubmit={() => ""}>
      <FormLayout>
        <div className="form-warnings">
          {errMsg && (
            <div className="">
              <Banner>{t(errMsg)}</Banner>
            </div>
          )}
          {successMessage && (
            <div className="">
              <Banner>{t(successMessage)}</Banner>
            </div>
          )}
          {parentCategoryError && (
            <Banner tone="critical">{parentCategoryError}</Banner>
          )}
        </div>
        <TextField
          label={t("files_category_title")}
          id="title"
          name="title"
          value={title}
          onChange={(value) => setTitle(value)}
          error={errors.title}
          autoComplete="off"
        />

        <Select
          label={t("type")}
          options={typeOptions}
          onChange={(value) => setType(value)}
          value={type}
          error={errors.type}
        />

        <FileCategorySingleSelect
          onChange={setParentCategory}
          value={parentCategory}
        />

        <TextField
          label={t("category_order")}
          id="order"
          name="order"
          value={order}
          onChange={(val) => {
            // allow only digits or empty
            if (/^\d*$/.test(val)) {
              setOrder(val);
            }
          }}
          error={errors.order}
          autoComplete="off"
        />

        <TextField
          label={t("required_within_days")}
          id="required_within_days"
          name="required_within_days"
          value={requiredWithinDays}
          onChange={(value) => setRequiredWithDays(value)}
          error={errors.requiredWithinDays}
          autoComplete="off"
        />

        <MultipleUserSelect
          onChange={setReviewRequiredUser}
          value={reviewRequiredUser}
          label={t("review_required_by")}
          isUserGuest={false}
        />
        <MultipleUserSelect
          onChange={setResponsibleUser}
          value={responsibleUser}
          label={t("responsible_user")}
          isUserGuest={false}
        />
        <MultipleSignatureSelect
          onChange={setSignatureTypes}
          value={signatureTypes}
          label={t("select_signature_types")}
        />
        <Checkbox
          label={t("is_shared_category")}
          checked={isShared}
          onChange={handleChange(setIsShared)}
        />

        <Checkbox
          label={t("is_visible_in_profile")}
          checked={isVisibleInProfile}
          onChange={handleChange(setIsVisibleInProfile)}
        />

        <Button
          onClick={handleFormSubmit}
          disabled={!!parentCategoryError}
          loading={isLoading}
        >
          {t("save")}
        </Button>
      </FormLayout>
    </Form>
  );
};

export default AddFilesCategoryForm;
